import clsx from "clsx";
import {
  Bell,
  LayoutGrid,
  LogOut,
  Map as MapIcon,
  Radio,
  Settings,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";

import { useAuth } from "@/lib/auth";
import { useLive, type LiveStatus } from "@/lib/live";
import { useAlertSummary } from "@/lib/queries";

import { StatusBadge } from "./ui";

const LIVE: Record<LiveStatus, { level: "good" | "warning" | "neutral"; label: string; title: string }> = {
  live: { level: "good", label: "Live", title: "Changes appear as they happen" },
  connecting: { level: "neutral", label: "Connecting", title: "Opening the live connection" },
  reconnecting: {
    level: "warning",
    label: "Reconnecting",
    title: "The live connection dropped. What you see may be out of date until it is back.",
  },
  offline: { level: "neutral", label: "Not live", title: "No live connection" },
};

function NavItem({
  to,
  icon: Icon,
  label,
  badge,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        clsx(
          "flex items-center gap-2.5 rounded-lg px-3 py-2 font-medium transition-colors",
          isActive ? "bg-white/8 text-ink" : "text-ink-2 hover:bg-white/5 hover:text-ink",
        )
      }
    >
      <Icon size={17} aria-hidden />
      <span className="flex-1">{label}</span>
      {badge ? (
        <span
          className="numeric rounded-full bg-critical px-1.5 text-xs font-semibold text-white"
          aria-label={`${badge} need attention`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
    </NavLink>
  );
}

export function Layout() {
  const { user, signOut, can } = useAuth();
  const live = useLive();
  const alerts = useAlertSummary();
  const connection = LIVE[live.status];

  return (
    <div className="flex min-h-full flex-col md:flex-row">
      <aside className="flex shrink-0 flex-col border-b border-line bg-surface md:min-h-screen md:w-56 md:border-b-0 md:border-r">
        <div className="flex items-center gap-2 px-4 py-4">
          <span className="grid size-7 place-items-center rounded-md bg-accent">
            <ShieldCheck size={17} aria-hidden />
          </span>
          <span className="text-[15px] font-semibold">DroidManage</span>
        </div>

        <nav className="flex gap-1 overflow-x-auto px-2 pb-2 md:flex-col md:overflow-visible" aria-label="Main">
          <NavItem to="/" icon={LayoutGrid} label="Fleet" />
          <NavItem to="/map" icon={MapIcon} label="Map" />
          <NavItem to="/commands" icon={Radio} label="Broadcast" />
          <NavItem to="/alerts" icon={Bell} label="Alerts" badge={alerts.data?.unacknowledged} />
          {can("operator") && <NavItem to="/admin" icon={Settings} label="Admin" />}
        </nav>

        <div className="mt-auto hidden border-t border-line p-3 md:block">
          <StatusBadge level={connection.level} label={connection.label} title={connection.title} />
          <div className="mt-3 truncate font-medium" title={user?.email}>
            {user?.full_name || user?.email}
          </div>
          <div className="flex items-center justify-between text-[13px] text-muted">
            <span className="capitalize">{user?.role}</span>
            <button
              type="button"
              onClick={signOut}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-ink-2 hover:bg-white/5 hover:text-ink"
            >
              <LogOut size={14} aria-hidden />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* On a phone the sidebar's footer moves up here. */}
        <div className="flex items-center justify-between border-b border-line px-4 py-2 md:hidden">
          <StatusBadge level={connection.level} label={connection.label} title={connection.title} />
          <button
            type="button"
            onClick={signOut}
            className="inline-flex items-center gap-1 text-ink-2 hover:text-ink"
          >
            <LogOut size={14} aria-hidden />
            Sign out
          </button>
        </div>
        {live.status === "reconnecting" && (
          <div role="status" className="border-b border-line bg-raised px-5 py-2 text-[13px] text-ink-2">
            The live connection dropped; reconnecting. What you see may be out of date.
          </div>
        )}
        <main className="mx-auto w-full max-w-[1500px] flex-1 px-4 py-5 md:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
